import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, GraduationCap, Users as UsersIcon, Briefcase, Search, Building2, Link2, ExternalLink, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCHOOL_TYPES = ['University', 'Community College', 'PT Program', 'PTA Program', 'OT Program', 'COTA Program', 'SLP Program', 'Career Fair', 'Other'];
const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'Other'];

function NewSchoolDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', school_type: 'University', city: '', state: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error('School name required'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/schools`, form);
      toast.success('School created');
      onCreated?.(res.data);
      onClose();
      setForm({ name: '', school_type: 'University', city: '', state: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create school');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>New School</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="new-school-name" /></div>
          <div>
            <Label>Type</Label>
            <select value={form.school_type} onChange={e => setForm({ ...form, school_type: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {SCHOOL_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} maxLength={2} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-new-school">{saving ? 'Saving…' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewProspectDialog({ open, onClose, onCreated, schools }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', discipline: '', school_id: '', graduating_year: '', graduating_term: '', source: '', notes_text: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) { toast.error('First and last name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.graduating_year) payload.graduating_year = parseInt(payload.graduating_year);
      else delete payload.graduating_year;
      if (!payload.school_id) delete payload.school_id;
      const res = await axios.post(`${API}/prospects`, payload);
      toast.success('Prospect added');
      onCreated?.(res.data);
      onClose();
      setForm({ first_name: '', last_name: '', email: '', phone: '', discipline: '', school_id: '', graduating_year: '', graduating_term: '', source: '', notes_text: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create prospect');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>New Prospect</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="prospect-first-name" /></div>
            <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="prospect-last-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Discipline</Label>
              <select value={form.discipline} onChange={e => setForm({ ...form, discipline: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">—</option>
                {DISCIPLINES.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label>School</Label>
              <select value={form.school_id} onChange={e => setForm({ ...form, school_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">—</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Graduating Year</Label>
              <Input type="number" value={form.graduating_year} onChange={e => setForm({ ...form, graduating_year: e.target.value })} placeholder="2026" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Term</Label>
              <select value={form.graduating_term} onChange={e => setForm({ ...form, graduating_term: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">—</option>
                <option>Spring</option><option>Summer</option><option>Fall</option><option>Winter</option>
              </select>
            </div>
            <div><Label>Source</Label><Input value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} placeholder="Career Fair, Referral, …" /></div>
          </div>
          <div>
            <Label>Initial Notes</Label>
            <Textarea value={form.notes_text} onChange={e => setForm({ ...form, notes_text: e.target.value })} rows={3} />
          </div>
          <div className="flex justify-end gap-2 pt-3">
            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-new-prospect">{saving ? 'Saving…' : 'Create Prospect'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Recruiting() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('schools');
  const [schools, setSchools] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [search, setSearch] = useState('');
  const [showNewSchool, setShowNewSchool] = useState(false);
  const [showNewProspect, setShowNewProspect] = useState(false);

  const fetchAll = async () => {
    try {
      const [s, p] = await Promise.all([
        axios.get(`${API}/schools`),
        axios.get(`${API}/prospects`),
      ]);
      setSchools(s.data || []);
      setProspects(p.data || []);
    } catch (_) { /* silent */ }
    try {
      const j = await axios.get(`${API}/ats/job-postings`);
      setJobPostings(j.data || []);
    } catch (_) { setJobPostings([]); }
  };

  useEffect(() => { fetchAll(); }, []);

  const filteredSchools = useMemo(() => {
    if (!search.trim()) return schools;
    const s = search.toLowerCase();
    return schools.filter(x => (x.name || '').toLowerCase().includes(s) || (x.city || '').toLowerCase().includes(s));
  }, [schools, search]);

  const filteredProspects = useMemo(() => {
    if (!search.trim()) return prospects;
    const s = search.toLowerCase();
    return prospects.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(s) || (p.email || '').toLowerCase().includes(s) || (p.school_name || '').toLowerCase().includes(s));
  }, [prospects, search]);

  const promoteProspect = async (p) => {
    if (!window.confirm(`Promote ${p.first_name} ${p.last_name} from Prospect to Applicant? They'll move to your active ATS pipeline.`)) return;
    try {
      await axios.post(`${API}/prospects/${p.id}/promote-to-applicant`);
      toast.success('Promoted to Applicant');
      fetchAll();
      navigate(`/ats/applicants/${p.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to promote');
    }
  };

  const publicFormUrl = `${window.location.origin}/recruiting/apply`;

  return (
    <div className="container mx-auto py-6 space-y-6 px-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><GraduationCap size={22} /> Recruiting & Marketing</h1>
          <p className="text-sm text-muted-foreground">Job postings, schools we recruit from, and the prospects we're cultivating.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/recruiting/prospects')} data-testid="open-licensed-prospects" className="gap-1">
            <UsersIcon size={13} /> Licensed prospects DB
          </Button>
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(publicFormUrl); toast.success('Public form URL copied!'); }} data-testid="copy-public-form-url" className="gap-1">
            <Link2 size={13} /> Copy public form link
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.open(publicFormUrl, '_blank', 'noopener,noreferrer')}
            data-testid="preview-public-form"
            className="gap-1 text-primary"
          >
            <ExternalLink size={13} /> Preview
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Filter ${tab}…`} className="pl-8" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-xl">
          <TabsTrigger value="schools" data-testid="tab-schools">
            <GraduationCap size={14} className="mr-1" /> Schools ({schools.length})
          </TabsTrigger>
          <TabsTrigger value="prospects" data-testid="tab-prospects">
            <UsersIcon size={14} className="mr-1" /> Prospects ({prospects.length})
          </TabsTrigger>
          <TabsTrigger value="job-postings" data-testid="tab-job-postings">
            <Briefcase size={14} className="mr-1" /> Job Postings ({jobPostings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="schools" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setShowNewSchool(true)} data-testid="new-school-btn"><Plus size={14} className="mr-1" /> New School</Button>
          </div>
          <Card className="p-0 overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/40 text-left text-xs">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Location</th>
                  <th className="px-4 py-2 text-right">Prospects</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredSchools.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-8 text-muted-foreground">No schools yet. Click "New School" to add one.</td></tr>
                ) : filteredSchools.map(s => (
                  <tr key={s.id} className="border-t hover:bg-muted/20" data-testid={`school-row-${s.id}`}>
                    <td className="px-4 py-2 font-medium"><Link to={`/recruiting/schools/${s.id}`} className="hover:underline">{s.name}</Link></td>
                    <td className="px-4 py-2 text-muted-foreground">{s.school_type}</td>
                    <td className="px-4 py-2 text-muted-foreground">{[s.city, s.state].filter(Boolean).join(', ') || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-700 text-xs">{s.prospect_count ?? 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="prospects" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setShowNewProspect(true)} data-testid="new-prospect-btn"><Plus size={14} className="mr-1" /> New Prospect</Button>
          </div>
          <Card className="p-0 overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/40 text-left text-xs">
                <tr>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Discipline</th>
                  <th className="px-4 py-2">School</th>
                  <th className="px-4 py-2">Grad</th>
                  <th className="px-4 py-2">Source</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredProspects.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No prospects yet.</td></tr>
                ) : filteredProspects.map(p => (
                  <tr key={p.id} className="border-t hover:bg-muted/20" data-testid={`prospect-row-${p.id}`}>
                    <td className="px-4 py-2 font-medium">
                      <Link to={`/ats/applicants/${p.id}`} className="hover:underline">{p.first_name} {p.last_name}</Link>
                      {p.email && <div className="text-xs text-muted-foreground">{p.email}</div>}
                    </td>
                    <td className="px-4 py-2">{p.discipline || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{p.school_name || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{p.graduating_year ? `${p.graduating_term || ''} ${p.graduating_year}`.trim() : '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground text-xs">{p.source || '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => promoteProspect(p)} data-testid={`promote-prospect-${p.id}`} className="gap-1">
                        <ArrowRight size={12} /> Promote
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="job-postings" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => navigate('/ats/job-postings')} data-testid="manage-job-postings-btn"><Plus size={14} className="mr-1" /> Manage in ATS</Button>
          </div>
          <Card className="p-0 overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted/40 text-left text-xs">
                <tr><th className="px-4 py-2">Title</th><th className="px-4 py-2">Status</th><th className="px-4 py-2">Discipline</th><th className="px-4 py-2">Applicants</th></tr>
              </thead>
              <tbody className="text-sm">
                {jobPostings.length === 0 ? (
                  <tr><td colSpan="4" className="text-center py-8 text-muted-foreground">No job postings.</td></tr>
                ) : jobPostings.map(j => (
                  <tr key={j.id} className="border-t hover:bg-muted/20">
                    <td className="px-4 py-2 font-medium">{j.title}</td>
                    <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded ${j.status === 'Open' || j.status === 'Active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{j.status}</span></td>
                    <td className="px-4 py-2 text-muted-foreground">{j.discipline || '—'}</td>
                    <td className="px-4 py-2 text-muted-foreground">{j.applicant_count ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <NewSchoolDialog open={showNewSchool} onClose={() => setShowNewSchool(false)} onCreated={fetchAll} />
      <NewProspectDialog open={showNewProspect} onClose={() => setShowNewProspect(false)} onCreated={fetchAll} schools={schools} />
    </div>
  );
}
